import { db, functions } from '@/lib/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, limit, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { sendPushNotification } from './pushNotifications';

/**
 * Admin Notification Service
 * Admin Notification Service
 * Handles cross-platform notification delivery (Desktop, Mobile)
 */

// Notification types
export const NOTIFICATION_TYPES = {
  ANNOUNCEMENT: 'announcement',
  UPDATE: 'update', 
  WARNING: 'warning',
  PROMOTION: 'promotion',
  SYSTEM: 'system',
  SUPPORT_RESPONSE: 'support_response'
};

// Target audiences that share one Firestore doc across many users (must respect status: sent)
export const DASHBOARD_BROADCAST_AUDIENCES = ['all', 'artists', 'karaoke'];

export function isDashboardBroadcastNotification(notification) {
  return notification && DASHBOARD_BROADCAST_AUDIENCES.includes(notification.targetAudience);
}

/** Broadcast announcements: only after admin hits Send (status sent). Direct/system stay visible without that. */
function shouldAppearInDashboardBell(notification) {
  const st = String(notification.status || '').toLowerCase();
  if (st === 'draft' || st === 'failed') return false;
  if (isDashboardBroadcastNotification(notification)) {
    return st === 'sent';
  }
  return true;
}

export const TARGET_AUDIENCES = {
  ALL: 'all',
  ARTISTS: 'artists',
  KARAOKE: 'karaoke',
  SPECIFIC: 'specific',
};

/**
 * Profiles that receive artist-channel broadcasts in the bell (see UserNotificationCenter).
 * Includes legacy profiles with no account_type (defaults to artist in the app), excludes karaoke/admin/agent.
 */
function profileMatchesArtistBroadcastAudience(profile) {
  const t = profile.account_type;
  if (t === 'admin') return false;
  if (t === 'karaoke') return false;
  if (t === 'agent') return false;
  if (t === 'artist') return true;
  if (!t && !profile.karaoke_host_id) return true;
  return false;
}

/**
 * Create a new admin notification
 * @param {Object} notificationData - The notification data
 * @param {string} notificationData.title - Notification title
 * @param {string} notificationData.message - Notification message
 * @param {string} notificationData.type - Notification type
 * @param {string} notificationData.targetAudience - Target audience
 * @param {Array} notificationData.targetUserIds - Specific user IDs (if targetAudience is 'specific')
 * @param {string} notificationData.createdBy - Admin user ID
 * @returns {Promise<Object>} Created notification with ID
 */
export const createAdminNotification = async (notificationData) => {
  try {
    console.log('📝 Creating admin notification:', notificationData);
    
    const notification = {
      title: notificationData.title,
      message: notificationData.message,
      type: notificationData.type || NOTIFICATION_TYPES.ANNOUNCEMENT,
      targetAudience: notificationData.targetAudience || TARGET_AUDIENCES.ALL,
      targetUserIds: notificationData.targetUserIds || [],
      createdBy: notificationData.createdBy,
      createdAt: new Date(),
      sentAt: null,
      readCount: 0,
      totalSent: 0,
      recipientCount: 0,
      status: 'draft' // draft, sent, cancelled
    };

    const docRef = await addDoc(collection(db, 'admin_notifications'), notification);
    console.log('✅ Admin notification created with ID:', docRef.id);
    
    return {
      id: docRef.id,
      ...notification
    };
  } catch (error) {
    console.error('❌ Error creating admin notification:', error);
    throw error;
  }
};

/**
 * Send notification to target audience
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} Send result with delivery stats
 */
export const sendAdminNotification = async (notificationId) => {
  try {
    console.log('📤 Sending admin notification:', notificationId);
    
    // Get the notification
    const notificationDoc = await getDoc(doc(db, 'admin_notifications', notificationId));
    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }
    
    const notification = notificationDoc.data();
    
    // Get target users based on audience
    const targetUsers = await getTargetUsers(notification);
    console.log(`🎯 Target users found: ${targetUsers.length}`);
    
    if (targetUsers.length === 0) {
      throw new Error('No target users found');
    }
    
    // Send push notifications to all target users
    let sentCount = 0;
    let failedCount = 0;
    
    for (const user of targetUsers) {
      try {
        // Send push notification
        const pushResult = await sendPushNotificationToUser(user.id, {
          title: notification.title,
          body: notification.message,
          data: {
            type: 'admin_notification',
            notificationId: notificationId,
            notificationType: notification.type,
            url: '/admin/notifications'
          }
        });
        
        if (pushResult) {
          sentCount++;
        } else {
          failedCount++;
        }
      } catch (error) {
        console.error(`❌ Failed to send to user ${user.id}:`, error);
        failedCount++;
      }
    }
    
    // Update notification with send status
    const recipientCount = targetUsers.length;
    await updateDoc(doc(db, 'admin_notifications', notificationId), {
      sentAt: new Date(),
      totalSent: sentCount,
      recipientCount,
      status: 'sent'
    });
    
    console.log(`✅ Notification sent: ${sentCount} successful, ${failedCount} failed`);
    
    return {
      success: true,
      sentCount,
      failedCount,
      totalTargets: targetUsers.length,
      targetAudience: notification.targetAudience,
    };
    
  } catch (error) {
    console.error('❌ Error sending admin notification:', error);
    
    // Update notification with error status
    try {
      await updateDoc(doc(db, 'admin_notifications', notificationId), {
        status: 'failed',
        error: error.message
      });
    } catch (updateError) {
      console.error('❌ Error updating notification status:', updateError);
    }
    
    throw error;
  }
};

/**
 * Get target users based on notification audience
 * @param {Object} notification - The notification object
 * @returns {Promise<Array>} Array of target users
 */
const getTargetUsers = async (notification) => {
  try {
    let usersQuery;
    
    switch (notification.targetAudience) {
      case TARGET_AUDIENCES.ALL:
        // Get all users
        usersQuery = query(collection(db, 'profiles'));
        break;
        
      case TARGET_AUDIENCES.ARTISTS: {
        // Must match bell feed: artist + legacy profiles missing account_type (not karaoke/admin/agent)
        const artistsSnap = await getDocs(collection(db, 'profiles'));
        return artistsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(profileMatchesArtistBroadcastAudience);
      }
        
      case TARGET_AUDIENCES.KARAOKE:
        // Get only karaoke users
        usersQuery = query(
          collection(db, 'profiles'),
          where('account_type', '==', 'karaoke')
        );
        break;
        
      case TARGET_AUDIENCES.SPECIFIC:
        // Get specific users by IDs
        if (!notification.targetUserIds || notification.targetUserIds.length === 0) {
          return [];
        }
        
        const specificUsers = [];
        for (const userId of notification.targetUserIds) {
          try {
            const userDoc = await getDoc(doc(db, 'profiles', userId));
            if (userDoc.exists()) {
              specificUsers.push({ id: userId, ...userDoc.data() });
            }
          } catch (error) {
            console.error(`❌ Error fetching user ${userId}:`, error);
          }
        }
        return specificUsers;
        
      default:
        return [];
    }
    
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error('❌ Error getting target users:', error);
    return [];
  }
};

/**
 * Send push notification to a specific user
 * @param {string} userId - The user ID
 * @param {Object} notificationData - The notification data
 * @returns {Promise<boolean>} Success status
 */
const sendPushNotificationToUser = async (userId, notificationData) => {
  try {
    // Get user's push subscription
    const subscriptionDoc = await getDoc(doc(db, 'push_subscriptions', userId));
    
    if (!subscriptionDoc.exists()) {
      console.log(`⚠️ No push subscription found for user ${userId}`);
      return false;
    }
    
    const subscription = subscriptionDoc.data();
    
    // Send the push notification
    const result = await sendPushNotification(subscription, notificationData);
    
    if (result) {
      console.log(`✅ Push notification sent to user ${userId}`);
      return true;
    } else {
      console.log(`❌ Failed to send push notification to user ${userId}`);
      return false;
    }
    
  } catch (error) {
    console.error(`❌ Error sending push notification to user ${userId}:`, error);
    return false;
  }
};

/**
 * Get all admin notifications
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of notifications to fetch
 * @param {string} options.orderBy - Field to order by
 * @param {string} options.orderDirection - Order direction (asc/desc)
 * @returns {Promise<Array>} Array of notifications
 */
export const getAdminNotifications = async (options = {}) => {
  try {
    console.log('🔍 Getting admin notifications...');
    const {
      limit: limitCount = 50,
      orderBy: orderByField = 'createdAt',
      orderDirection = 'desc'
    } = options;
    
    const notificationsQuery = query(
      collection(db, 'admin_notifications'),
      orderBy(orderByField, orderDirection),
      limit(limitCount)
    );
    
    console.log('🔍 Executing Firestore query...');
    const snapshot = await getDocs(notificationsQuery);
    console.log('✅ Firestore query successful, found', snapshot.docs.length, 'notifications');
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error('❌ Error getting admin notifications:', error);
    console.error('❌ Error details:', error.code, error.message);
    throw error;
  }
};

/**
 * Mark notification as read by user (server: receipt + readCount increment in one transaction).
 */
export const markNotificationAsRead = async (notificationId, userId) => {
  try {
    if (!notificationId || !userId) return false;
    if (!functions) {
      console.error('❌ markNotificationAsRead: Firebase Functions not initialized');
      return false;
    }
    const recordRead = httpsCallable(functions, 'recordAdminNotificationRead');
    await recordRead({ notificationId });
    console.log(`✅ Notification ${notificationId} marked read (server) for user ${userId}`);
    return true;
  } catch (error) {
    console.error('❌ Error marking notification as read:', error?.code, error?.message, error);
    return false;
  }
};

/**
 * All notifications for the dashboard bell (read + unread), newest first.
 * Each item includes `read: boolean` from `notification_reads`.
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export const getUserBellNotifications = async (userId) => {
  try {
    console.log('🔍 Getting bell notifications for user:', userId);
    
    // First, get the user's profile to find their signup date and account type
    const userProfileRef = doc(db, 'profiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    let userSignupDate = null;
    let userAccountType = null;
    if (userProfileSnap.exists()) {
      const userData = userProfileSnap.data();
      userSignupDate = userData.created_at ? new Date(userData.created_at) : null;
      userAccountType = userData.account_type || 'artist';
      console.log('🔍 User signup date:', userSignupDate);
      console.log('🔍 User account type:', userAccountType);
    }
    
    // ✅ FIX: Query notifications with proper filtering to respect Firestore security rules
    // We need multiple queries because Firestore rules check per-document, not after client-side filtering
    const notificationQueries = [];
    
    // Query 1: Notifications specifically targeted to this user
    notificationQueries.push(
      query(
        collection(db, 'admin_notifications'),
        where('targetUserId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(50)
      )
    );
    
    // Query 2: Broadcast notifications (targetAudience = 'all')
    notificationQueries.push(
      query(
        collection(db, 'admin_notifications'),
        where('targetAudience', '==', 'all'),
        orderBy('createdAt', 'desc'),
        limit(50)
      )
    );
    
    // Query 3: Account type specific notifications
    if (userAccountType === 'artist') {
      notificationQueries.push(
        query(
          collection(db, 'admin_notifications'),
          where('targetAudience', '==', 'artists'),
          orderBy('createdAt', 'desc'),
          limit(50)
        )
      );
    } else if (userAccountType === 'karaoke') {
      notificationQueries.push(
        query(
          collection(db, 'admin_notifications'),
          where('targetAudience', '==', 'karaoke'),
          orderBy('createdAt', 'desc'),
          limit(50)
        )
      );
    }

    // Query 4: Notifications that list this uid (referrals, etc.)
    notificationQueries.push(
      query(
        collection(db, 'admin_notifications'),
        where('targetUserIds', 'array-contains', userId),
        orderBy('createdAt', 'desc'),
        limit(50)
      )
    );

    // Execute all queries in parallel (they may fail individually, so we catch errors)
    const allNotificationPromises = notificationQueries.map(async (q) => {
      try {
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        // Handle permission errors gracefully - some queries may not have indexes yet
        if (error.code === 'failed-precondition') {
          console.warn('⚠️ Query requires composite index. Fetching without orderBy...');
          // Try without orderBy if index doesn't exist
          try {
            const simpleQuery = query(
              collection(db, 'admin_notifications'),
              q._queryConstraints[0] // Just the where clause
            );
            const snapshot = await getDocs(simpleQuery);
            return snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => {
              // Sort client-side
              const dateA = a.createdAt?.toDate?.() || new Date(0);
              const dateB = b.createdAt?.toDate?.() || new Date(0);
              return dateB - dateA;
            });
          } catch (fallbackError) {
            console.warn('⚠️ Fallback query also failed:', fallbackError.message);
            return [];
          }
        } else if (error.code === 'permission-denied') {
          console.warn('⚠️ Permission denied for notification query:', error.message);
          return [];
        }
        console.warn('⚠️ Error executing notification query:', error.message);
        return [];
      }
    });
    
    const queryResults = await Promise.all(allNotificationPromises);
    const allNotifications = queryResults.flat();
    
    // Remove duplicates (same notification might match multiple queries)
    const uniqueNotifications = Array.from(
      new Map(allNotifications.map(n => [n.id, n])).values()
    );
    
    console.log('🔍 Found', uniqueNotifications.length, 'total unique notifications');
    
    // Filter notifications that are targeted to this user AND created after their signup
    const targetedNotifications = uniqueNotifications.filter(notification => {
      if (!shouldAppearInDashboardBell(notification)) {
        return false;
      }

      // ✅ Verify targeting (already filtered by queries, but double-check for specific notifications)
      let isTargeted = false;
      if (notification.targetAudience === 'all') {
        isTargeted = true;
      } else if (notification.targetAudience === 'artists') {
        isTargeted = userAccountType === 'artist';
      } else if (notification.targetAudience === 'karaoke') {
        isTargeted = userAccountType === 'karaoke';
      } else if (notification.targetAudience === 'specific') {
        // For specific notifications, check if user ID is in the array
        // Note: We can't query array-contains efficiently with security rules,
        // so these will be fetched via targetUserId query or manual check
        isTargeted = (notification.targetUserIds && notification.targetUserIds.includes(userId)) ||
                     notification.targetUserId === userId;
      } else if (notification.targetUserId) {
        // Handle single user targeting (for support responses)
        isTargeted = notification.targetUserId === userId;
      }
      
      if (!isTargeted) {
        return false;
      }
      
      // If user has a signup date, only show notifications created after signup
      if (userSignupDate && notification.createdAt) {
        const notificationDate = notification.createdAt.toDate ? 
          notification.createdAt.toDate() : 
          new Date(notification.createdAt);
        
        const isAfterSignup = notificationDate >= userSignupDate;
        console.log('🔍 Notification date:', notificationDate, 'vs signup date:', userSignupDate, 'isAfterSignup:', isAfterSignup);
        
        // For system notifications (like referrals), always show them regardless of signup date
        // as they are user-specific actions that happened after the user signed up
        if (notification.type === 'system') {
          return true;
        }
        
        return isAfterSignup;
      }
      
      // If no signup date available, show all targeted notifications (fallback)
      return true;
    });
    
    console.log('🔍 Found', targetedNotifications.length, 'targeted notifications after signup filtering');
    
    // Get read notifications for this user
    const readQuery = query(
      collection(db, 'notification_reads'),
      where('userId', '==', userId)
    );
    
    const readSnapshot = await getDocs(readQuery);
    const readNotificationIds = readSnapshot.docs.map(doc => doc.data().notificationId);
    
    console.log('🔍 Found', readNotificationIds.length, 'read notifications');
    
    const withRead = targetedNotifications.map((notification) => ({
      ...notification,
      read: readNotificationIds.includes(notification.id),
    }));

    withRead.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });

    console.log('🔍 Returning', withRead.length, 'bell notifications (read + unread)');

    return withRead;
    
  } catch (error) {
    console.error('❌ Error getting bell notifications:', error);
    return [];
  }
};

/**
 * Get unread notifications for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of unread notifications
 */
export const getUnreadNotifications = async (userId) => {
  const all = await getUserBellNotifications(userId);
  return all.filter((n) => !n.read);
};

/**
 * Delete an admin notification
 * @param {string} notificationId - The notification ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteAdminNotification = async (notificationId) => {
  try {
    // Delete the notification
    await deleteDoc(doc(db, 'admin_notifications', notificationId));
    
    // Delete all read receipts for this notification
    const readQuery = query(
      collection(db, 'notification_reads'),
      where('notificationId', '==', notificationId)
    );
    
    const readSnapshot = await getDocs(readQuery);
    const deletePromises = readSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    console.log(`✅ Notification ${notificationId} deleted`);
    return true;
    
  } catch (error) {
    console.error('❌ Error deleting admin notification:', error);
    return false;
  }
};
